import { Page, Text, Link, View, Image } from '@react-pdf/renderer';
import React from 'react';
import { ScenarioPdfQuery } from 'src/graphql/_generated';
import { Header } from './Header';
import { pdfStyles } from '../pdfStyles';
import { Footer } from './Footer';
import { NoData } from './NoData';
import { TitleLink } from './TitleLink';
import { ScenariosTable } from './ScenariosTable';
import { extLinkImg } from '../imgData';
import { ColorSet } from 'src/redux/reducers/theme';
import { formatDate } from 'src/utils/misc';
import { ActionsTable } from './ActionsTable';
import { ActionsConf } from '../types';
import { PdfLinkifyText } from './PdfLinkifyText';

type IncidentGql = NonNullable<ScenarioPdfQuery['scenario']>['incidents'][number];

interface IncidentPageProps {
  incident: IncidentGql;
  onRender: (pageNumber: number) => void;
  endComponent?: React.ReactNode;
  footerComponent?: React.ReactNode;
  colors: ColorSet;
  includesActions: ActionsConf;
}

export const IncidentPage: React.FC<IncidentPageProps> = ({
  incident,
  onRender,
  endComponent,
  footerComponent,
  colors,
  includesActions,
}) => {
  return (
    <Page size={'A4'} style={pdfStyles.page}>
      <Header endComponent={endComponent} colors={colors} />

      {/* @ts-ignore */}
      <View render={(data) => onRender(data.pageNumber)} />
      <View style={pdfStyles.section}>
        <TitleLink
          title={incident.name}
          intLinkId={incident.name.toLowerCase()}
          extLink={`incidents/${incident.id}`}
          colors={colors}
        />

        <View style={pdfStyles.spacer8} />
        <View style={pdfStyles.spacer4} />

        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 220 }}>
            <Text style={pdfStyles.label}>REFERENCE ID</Text>
            <View style={pdfStyles.spacer1} />
            {incident.ref ? (
              incident.source ? (
                <Link src={incident.source} style={{ flexDirection: 'row', textDecoration: 'none' }}>
                  <Text style={[pdfStyles.value, { textDecoration: 'underline' }]}>{incident.ref}</Text>
                  {/* <Image src={extLinkImg} style={{ marginLeft: 4, width: 8, height: 8, marginTop: 3 }} /> */}
                </Link>
              ) : (
                <Text style={pdfStyles.value}>{incident.ref}</Text>
              )
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>

        <View style={pdfStyles.spacer8} />
        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 220 }}>
            <Text style={pdfStyles.label}>OWNER</Text>
            <View style={pdfStyles.spacer1} />
            {incident.owner ? (
              <Text style={pdfStyles.value}>{(incident.owner.text || incident.owner.email || '-').split('')}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View>
            <Text style={pdfStyles.label}>DATE OF INCIDENT</Text>
            <View style={pdfStyles.spacer1} />
            {incident.dateOfIncident ? (
              <Text style={pdfStyles.value}>{formatDate(incident.dateOfIncident, { emptyValue: '-' })}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>

        <View style={pdfStyles.spacer8} />
        <View style={{ width: 220 }}>
          <Text style={pdfStyles.label}>IMPACT</Text>
          <View style={pdfStyles.spacer1} />
          {incident.impact ? <PdfLinkifyText>{incident.impact}</PdfLinkifyText> : <NoData colors={colors} />}
        </View>
        <View style={pdfStyles.spacer8} />
        <Text style={pdfStyles.label}>DESCRIPTION</Text>
        <View style={pdfStyles.spacer1} />
        {incident.description ? <PdfLinkifyText>{incident.description}</PdfLinkifyText> : <NoData colors={colors} />}

        <View style={pdfStyles.spacer8} />
        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 220 }}>
            <Text style={pdfStyles.label}>IMPACT - LOWER</Text>
            <View style={pdfStyles.spacer1} />
            {incident.financialImpactLower ? (
              <Text style={pdfStyles.value}>{incident.financialImpactLower}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View>
            <Text style={pdfStyles.label}>IMPACT - UPPER</Text>
            <View style={pdfStyles.spacer1} />
            {incident.financialImpactUpper ? (
              <Text style={pdfStyles.value}>{incident.financialImpactUpper}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>
        <View style={pdfStyles.spacer8} />
        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 220 }}>
            <Text style={pdfStyles.label}>TYPE</Text>
            <View style={pdfStyles.spacer1} />
            {incident.incidentType ? (
              <Text style={pdfStyles.value}>{incident.incidentType}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={pdfStyles.label}>SEVERITY</Text>
            <View style={pdfStyles.spacer1} />
            {incident.severity ? (
              <PdfLinkifyText style={pdfStyles.value}>{incident.severity}</PdfLinkifyText>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>

        <View style={pdfStyles.spacer8} />
        <View style={pdfStyles.spacer8} />
        {incident.scenarios?.length > 0 ? <ScenariosTable data={incident.scenarios} colors={colors} /> : null}
        <ActionsTable data={incident.actions} colors={colors} includesActions={includesActions} />
      </View>
      <Footer footerComponent={footerComponent} colors={colors} />
    </Page>
  );
};
